/** Knowledge › Blocks (knowledge→block plan 2): pure helpers for the panel. */
export const V2_LINK_CHANNELS = ['FACEBOOK', 'INSTAGRAM', 'TELEGRAM', 'VIBER', 'CUSTOM_CHANNEL'] as const;
export const CHANNEL_LABEL: Record<string, string> = {
  FACEBOOK: 'Messenger', INSTAGRAM: 'Instagram', TELEGRAM: 'Telegram', VIBER: 'Viber', CUSTOM_CHANNEL: 'Web widget',
};
const MAX_EXAMPLES = 10;

export interface BlockLinkRow {
  id: string; system: string; botId: string; channel: string | null; blockRef: string; title: string;
  description: string; examples: string[]; enabled: boolean; brokenAt: string | null; brokenReason: string | null;
  createdBy: string | null; updatedAt: string; lastSentAt?: string | null;
}
export interface LinkThresholds { blockOnly: number; attach: number }
export interface PickerBlock { ref: string; name: string; channel: string | null }

export function linkStatus(l: { enabled: boolean; brokenAt: string | null }): 'ok' | 'off' | 'broken' {
  if (l.brokenAt) return 'broken';
  return l.enabled ? 'ok' : 'off';
}

export function parseExamples(text: string): string[] {
  const out: string[] = [];
  for (const line of text.split('\n')) {
    const t = line.trim();
    if (t && !out.includes(t)) out.push(t);
  }
  return out.slice(0, MAX_EXAMPLES);
}

export const exampleHint = (n: number): string | null =>
  n < 3 ? 'Add 3–5 example questions so the AI can find this block.' : null;

export const scoreLabel = (score: number): string => score.toFixed(2);

/** Distinct non-empty example lines, without `parseExamples`' cap — so going over the limit is visible, not dropped. */
export function distinctExamples(text: string): number {
  return new Set(text.split('\n').map((l) => l.trim()).filter(Boolean)).size;
}

export const exampleLimitHint = (n: number): string | null =>
  n > MAX_EXAMPLES ? `At most ${MAX_EXAMPLES} examples — remove one before saving.` : null;

/** The examples text with `text` added as a new last line, unless it is already there (case-insensitive). */
export function withExample(examples: string[], text: string): string {
  const t = text.trim();
  const has = examples.some((e) => e.trim().toLowerCase() === t.toLowerCase());
  return (has || !t ? examples : [...examples, t]).join('\n');
}

const MIN_DESCRIPTION = 80;
export const descriptionHint = (description: string): string | null =>
  description.trim().length < MIN_DESCRIPTION
    ? 'Say when to send this block and when not — a short description matches too many questions.'
    : null;

export interface LinkStat { best: number; sent: number }

export const linkStatsLabel = (s: LinkStat | undefined, days: number): string =>
  `Closest for ${s?.best ?? 0} questions · sent ${s?.sent ?? 0} (last ${days} days)`;

/** A link that is on, while the bot did get customer questions, but was never the closest block for one of them. */
export const neverClosestHint = (l: { enabled: boolean }, s: LinkStat | undefined, decisions: number): string | null =>
  l.enabled && decisions > 0 && !s?.best
    ? 'Not the closest block for any customer question — check its examples in the Test box.'
    : null;

/** "Last sent: …" (plan 3): when the AI last sent this block; never → "never". */
export function lastSentLabel(iso: string | null | undefined, now: Date = new Date()): string {
  if (!iso) return 'never';
  const t = new Date(iso);
  if (Number.isNaN(t.getTime())) return 'never';
  const mins = Math.floor((now.getTime() - t.getTime()) / 60_000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} min ago`;
  if (mins < 24 * 60) return `${Math.floor(mins / 60)} h ago`;
  return t.toISOString().slice(0, 10);
}

/**
 * What the runtime would do with a test-box hit on its own (plan 3 thresholds). The runtime also compares it with the
 * knowledge found for the question, so this is the link's best case.
 */
export function hitBand(score: number, t: LinkThresholds): string {
  if (score >= t.blockOnly) return `Block only (≥ ${t.blockOnly.toFixed(2)})`;
  if (score >= t.attach) return `With the answer (≥ ${t.attach.toFixed(2)})`;
  return 'Below threshold';
}

// Mirror of the backend's isValidRef (backend/src/block-link/block-link.rules.ts).
const V1_REF = /^[A-Za-z0-9_]{1,64}$/;
const V2_REF = /^[1-9]\d{0,9}(:[1-9]\d{0,9})?$/;

/**
 * Deep link from the block editors: `?block=<ref>[&channel=<CHANNEL>]`. Returns the block to open, or null when the
 * ref is missing/invalid for the system or (v2) the channel is not a link channel. Anything invalid is dropped.
 * v1 has no channel (null).
 */
export function preselect(
  params: { get(name: string): string | null },
  system: string,
): { ref: string; channel: string | null } | null {
  const ref = params.get('block');
  if (!ref) return null;
  if (system === 'chatbot_v2') {
    const channel = params.get('channel');
    if (!V2_REF.test(ref) || !channel || !(V2_LINK_CHANNELS as readonly string[]).includes(channel)) return null;
    return { ref, channel };
  }
  if (system === 'chatbot' && V1_REF.test(ref)) return { ref, channel: null };
  return null;
}

/** One row of `GET block-links/bundle-text` (backend/src/block-link/bundle-coverage.ts). */
export interface BundleTextRow {
  bundleId: string;
  name: string;
  key: string;
  textPreview: string;
  foundIn: { documentId: string; title: string; score: number } | null;
}

/**
 * The "Before switching on" panel's numbers: how many bundles hold text, and which of them the documents lack.
 * `null` = nothing to show (the bot has no bundle text).
 */
export function bundleTextSummary(rows: BundleTextRow[], truncated = false): { sentence: string; missing: BundleTextRow[] } | null {
  if (rows.length === 0) return null;
  const missing = rows.filter((r) => !r.foundIn);
  const n = `${rows.length}${truncated ? '+' : ''}`;
  const one = rows.length === 1 && !truncated;
  return {
    sentence: one
      ? `1 bundle holds text the AI will stop reading when this bot is on. It is ${missing.length ? 'not' : 'already'} in your documents.`
      : `${n} bundles hold text the AI will stop reading when this bot is on. ${missing.length} of them are not in your documents.`,
    missing,
  };
}

/** Shown when the bundle-text check could not load: the panel must never vanish as if there were nothing to check. */
export function bundleTextLoadNotice(loadFailed: boolean): string | null {
  return loadFailed ? "Could not check bundle text — don't switch this bot on yet." : null;
}

/** Bundle edits are closed for a bot on `block_links=on` (the hub answers 409 bundles_retired); reads stay open. */
export const BUNDLES_RETIRED_NOTE = 'This bot uses Knowledge › Blocks. Message bundles are read-only.';
export function bundlesRetired(flow: Record<string, string> | null | undefined): boolean {
  return flow?.block_links === 'on';
}
